import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, type ReactNode, useEffect, useImperativeHandle, useRef } from 'react';

export type ClipFacesVirtualizedListProps = {
  itemsCount: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  children: (props: { index: number }) => ReactNode;
};

export type ClipFacesVirtualizedListHandle = {
  scrollToRow: (index: number) => void;
};

export const FACE_ROW_HEIGHT = 60;

const _ClipFacesVirtualizedList = forwardRef<ClipFacesVirtualizedListHandle, ClipFacesVirtualizedListProps>(
  (
    { itemsCount, hasNextPage, fetchNextPage, isFetchingNextPage, children }: ClipFacesVirtualizedListProps,
    forwardedRef,
  ) => {
    const scrollElementRef = useRef<HTMLDivElement | null>(null);

    const virtualizer = useVirtualizer({
      count: hasNextPage ? itemsCount + 1 : itemsCount,
      estimateSize: () => FACE_ROW_HEIGHT,
      getScrollElement: () => scrollElementRef.current,
      overscan: 10,
      scrollMargin: 8,
    });

    useImperativeHandle(forwardedRef, () => ({
      scrollToRow: (index) => virtualizer.scrollToIndex(index, { behavior: 'smooth' }),
    }));

    const virtualItems = virtualizer.getVirtualItems();

    useEffect(() => {
      const lastItem = virtualItems[virtualItems.length - 1];

      if (!lastItem) {
        return;
      }

      if (lastItem.index >= itemsCount - 1 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [hasNextPage, fetchNextPage, isFetchingNextPage, virtualItems, itemsCount]);

    return (
      <div
        data-testid="CLIP_FACES_LIST_SCROLL"
        ref={scrollElementRef}
        className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent w-full flex-1 overflow-y-auto pr-2 pl-4"
      >
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
          <div
            className="absolute top-0 left-0 w-full pb-4"
            style={{ transform: `translateY(${(virtualItems[0]?.start ?? 0) + virtualizer.options.scrollMargin}px)` }}
          >
            {virtualItems.map((virtualRow) => (
              <div key={virtualRow.key} data-index={virtualRow.index} ref={virtualizer.measureElement}>
                {children({ index: virtualRow.index })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

_ClipFacesVirtualizedList.displayName = '_ClipFacesVirtualizedList';

export const ClipFacesVirtualizedList = memo(_ClipFacesVirtualizedList);

ClipFacesVirtualizedList.displayName = 'ClipFacesVirtualizedList';
